<template lang="pug">
	.easy-creative--voice-adjectives.flex-col.space
		.flex-row.space.align-center
			.font-large {{$tCaption('Voice selection')}}
		.actions-divider.full-width
		.tb-padding
			Loader(v-if="isLoading", :text="$tCaption('Loading') + '..'")
			.flex-col.space(v-else)
				DxCheckBox(v-for="adjective in adjectives", :text="adjective", :value="selectedAdjectives.includes(adjective)", @value-changed="$Data.toggleSelectedIdInArray(adjective, selectedAdjectives)")
		.flex-col.space-medium.full-width
			.actions-divider.full-width
			.flex-row.max-limited.navigation-row.justify-end
				.flex-row.space
					DxButton(:text="$tCaption('Back', '@actions')", @click="$emit('back')", :activeStateEnabled="false", :focusStateEnabled="false")
					DxButton(:text="$tCaption('Next', '@actions')", type="default", @click="$emit('next')", :activeStateEnabled="false", :focusStateEnabled="false", :disabled="!!isLoading || !selectedAdjectives.length")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';
	import DxCheckBox from 'devextreme-vue/check-box';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-voice-adjectives',
		mixins: [cognitiveArrays],
		props: ['info'],
		components: {
			DxButton, Loader, DxCheckBox
		},
		data() {
			return {
				isLoading: false,
				adjectives: [],
				selectedAdjectives: [],
				adjectivesWeightsCoefs: {
					genre: 3,
					letter: 2,
					detailed: 1
				}
			};
		},
		computed: {
			
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreativeVoiceAdjectives = this;
			this.loadVoiceAdjectives();
		},
		methods: {
			async loadVoiceAdjectives() {
				this.isLoading++;
				this.adjectives = (await this.getVoicesAdjectives().finally(() => this.isLoading--));
			},
			getAcceptableVoices() {
				return this.findVoicesByAdjectives(this.selectedAdjectives);
			},
			applyFilters(voices) {
				const lang = this.info.language();
				let allowedTtsProviders = this.voiceTtsProviders.filter(f => f.AllowedForSelfService).map(f => f.ProviderType);
				return (voices || []).filter(f => allowedTtsProviders.includes(f?.voice?.TtsProviderType || 0)).filter(f => !lang || f.locale === lang?.locale);
			},
			getRandomVoice(opts) {
				const voices = this.applyFilters(opts?.voices || this.findVoicesByAdjectives(opts?.adjectives || this.selectedAdjectives));
				return voices[_.random(0, voices.length-1)];
			},
			calcVoiceAdjectivesWeight(request, voice) {
				let calcAdjectivesTotal = (adjs) => {
					if (!adjs?.length) return 0;

					let aimAdjectives = (voice?.voice?.Adjectives || []).filter(a => adjs.includes(a.Name));
					return aimAdjectives.reduce((total, v) => { total += v?.Value || 0; return total; } , 0)
				};

				let gnrTotal = calcAdjectivesTotal(request.genreAdjectives);
				let letterTotal = calcAdjectivesTotal(request.adjectives);
				let detailedTotal = calcAdjectivesTotal(request.detailedAdjectives);

				return gnrTotal * this.adjectivesWeightsCoefs.genre + letterTotal * this.adjectivesWeightsCoefs.letter + detailedTotal * this.adjectivesWeightsCoefs.detailed;
			},
			requestVoices(arr, logInfo) {
				const result = {};
				const excludedVoices = [];

				let debugVoicesFormatter = (request, voices) => {
					let aimAdjectives = [...(request.genreAdjectives || []), ...(request.adjectives || []), ...(request.detailedAdjectives || [])];
					return voices.map(f => `${f.displayName} Rtg.${f.voice.Rating}, Wgt.${this.calcVoiceAdjectivesWeight(request, f)} (${f.voice.Adjectives?.filter(a => aimAdjectives.includes(a.Name))?.map(a => `${a.Name} ${a.Value}`).join(', ') || 'no-adjectives'})`)
				}

				arr.map(f => {
					if (result[f.key]) return;
					let resultVoices = [];

					//request may have it's own filter (by gender), need to apply first to all voices
					let allowCustomVoices = this.info.allowedVoices() !== 1;

					let requestLog = {
						request: `${logInfo}, ${f.key}. ${f.genreAdjectives ? `Genre adj.: ${f.adjectives?.join(',') || '-'}. ` : ''}Adjectives: ${f.adjectives?.join(',') || '-'}. Detail adj.: ${f.detailedAdjectives?.join(',') || '-'}.`,
						steps: [],
						allowCustomVoices
					}

					let langAndTtsFiltered = this.applyFilters(this.voices);
					let genderFiltered = langAndTtsFiltered.filter(f?.filter || (f => f));
					let customFiltered = genderFiltered.filter(f => !f.isCustom || allowCustomVoices);
					let availableVoices = customFiltered;
					requestLog.steps.push({
						text: `filter by lang and allowed providers (${langAndTtsFiltered.length}), gender(${genderFiltered.length}), common/premium filtered(${customFiltered.length}). available voices: ${availableVoices.length}`,
						voices: debugVoicesFormatter(f, _.orderBy(availableVoices, v => v.voice.Rating, ['desc']))
					});

					let targetVoices = this.filterByVoiceTarget(f, availableVoices);

					let lang = targetVoices[0]?.language;
					let voiceTargetRating = this.info.branding?.VoiceTargetRating || lang?.voiceTargetRating || Math.max(0, _.max(targetVoices.map(f => f.voice.Rating)) - 3);
					let minCountVoices = Math.max(1, this.info.branding?.MinCountVoices || lang?.minCountVoices || 10);
					requestLog.steps.push({
						text: `filter by target rating. voices: ${targetVoices.length}`,
						voiceTargetRating,
						minCountVoices,
						voices: debugVoicesFormatter(f, targetVoices)
					});

					if (__DEBUG__)
						console.debug('requestVoices', { f, targetVoices, allowCustomVoices, r: targetVoices.some(f => f.isCustom), ratings: targetVoices.map(f => f.voice.Rating) });

					//Search allowed voices by adjectives
					if (!resultVoices.length) {
						let adjectivesWeightedVoices = _.orderBy(targetVoices.filter(v => !excludedVoices.includes(v)), (v) => this.calcVoiceAdjectivesWeight(f, v), ['desc']);
						let targetWeight = this.calcVoiceAdjectivesWeight(f, adjectivesWeightedVoices[0]) * voiceTargetRating / 10;

						let adjectivesWeightedVoicesTargetFiltered = adjectivesWeightedVoices.filter((v, i) => this.calcVoiceAdjectivesWeight(f, v) >= targetWeight || (i < minCountVoices));
						if (adjectivesWeightedVoicesTargetFiltered.length) {
							resultVoices = adjectivesWeightedVoicesTargetFiltered;
						}

						requestLog.steps.push({
							text: `search by adjectives: ${adjectivesWeightedVoicesTargetFiltered.length}`,
							voiceTargetRating,
							minCountVoices,
							targetWeight,
							adjectivesWeightsCoefs: this.adjectivesWeightsCoefs,
							voices: debugVoicesFormatter(f, adjectivesWeightedVoicesTargetFiltered)
						});
					}

					//If no results by adjectives - search by language only in target voices
					if (!resultVoices.length) {
						if (__DEBUG__)
							console.debug('requestVoices 3');
						resultVoices = this.getNumberedVoices({
							count: 1,
							excludedVoices,
							voices: targetVoices,
							filter: f.filter
						});

						requestLog.steps.push({
							text: `search in target-rating voices: ${targetVoices.length}`,
							voices: debugVoicesFormatter(f, targetVoices),
							result: debugVoicesFormatter(f, resultVoices),
						});
					}

					//If no results by target voices - search by language only for all voices
					if (!resultVoices.length) {
						if (__DEBUG__)
							console.debug('requestVoices 4');

						requestLog.steps.push({
							text: `search in all voices`
						});

						resultVoices = this.getNumberedVoices({
							count: 1,
							excludedVoices,
							voices: this.voices,
							filter: f.filter
						});
					}

					if (resultVoices.length) {
						const voice = this.selectVoice(resultVoices);
						excludedVoices.push(voice);
						result[f.key] = voice;
						
						requestLog.resultVoice = debugVoicesFormatter(f, [voice])[0];
						requestLog.steps.push({
							text: `Selected voice: ${debugVoicesFormatter(f, [voice])[0]}`,
							voices: debugVoicesFormatter(f, resultVoices)
						});

						requestLog.readingRate = {
							resultRate: voice?.readingRateInfo?.voiceReadingRate,
							calculations: {
								...voice?.readingRateInfo,
								voiceReadingRateFormula: 'langTotal / voiceWPM'
							}
						};
					}

					this.$eventsBus.$emit('easy-creative--request-voices--debug-info', requestLog);
				});

				if (__DEBUG__)
					console.debug('requestVoices 5 Request voices', { arr, result });

				return result;
			},
			filterByVoiceTarget(request, voices) {
				let lang = voices[0]?.language;
				let minCountVoices = Math.max(1, this.info.branding?.MinCountVoices || lang?.minCountVoices || 10);
				let voiceTargetRating = this.info.branding?.VoiceTargetRating || lang?.voiceTargetRating || Math.max(0, _.max(voices.map(f => f.voice.Rating)) - 3);

				if (voiceTargetRating) {
					voices = _.orderBy(voices, [f => f.voice.Rating || 0, f => this.calcVoiceAdjectivesWeight(request, f)], ['desc', 'desc']);

					if (__DEBUG__)
						console.debug('VoiceTargetRating', { totalVoices: voices.length, minCountVoices, voiceTargetRating, maxRating: _.max(voices.map(f => f.voice.Rating)), voices });
					
					voices = voices.filter((f, i) => (f.voice.Rating >= voiceTargetRating) || (i < minCountVoices));

					if (__DEBUG__)
						console.debug(`VoiceTargetRating filtered ${voices.length}`, { voices, ratings: voices.map(f => f.voice.Rating) })
				}

				return voices;
			},
			selectVoice(voices) {
				let randoms = [];
				for(let i = 0; i < 5; i++) {
					randoms.push(_.random(0, voices.length-1));
				}

				let random = _.shuffle(randoms)[_.random(0, randoms.length-1)];
				let voice = voices[random];
				
				if (__DEBUG__)
					console.debug(`VoiceTargetRating filtered voice ${voice?.displayName} ${random} (${randoms})`);

				return voice;
			},
			getNumberedVoices(opts) {
				if (!opts?.count) return [];

				const res = [];
				let voices = this.applyFilters((opts?.voices || this.findVoicesByAdjectives(opts?.adjectives || this.selectedAdjectives, opts?.filteredVoices?.length ? opts?.filteredVoices : null)))
					.filter(f => !opts?.baseFilter || opts.baseFilter(f))
					.filter(f => !opts?.excludedVoices?.length || !opts?.excludedVoices.includes(f))
					.filter(opts?.filter || (f => f));

				if (opts?.all) {
					if (__DEBUG__)
						console.debug('getNumberedVoices', { opts, voices, exclVoices: opts?.excludedVoices?.length });
					return voices;
				}
				
				for(let i = res.length; i < opts.count; i++) {
					let notIncludedVoices = voices.filter(f => !res.includes(f));
					if (notIncludedVoices.length) {
						res.push(this.selectVoice(notIncludedVoices));
					}
				}

				if (__DEBUG__)
					console.debug('getNumberedVoices', { opts, res, voices, exclVoices: opts?.excludedVoices?.length });
				return voices;
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--voice-adjectives {
		min-height: 300px;

		@media all and (min-width: 720px) {
			width: 720px;
		}
	}
</style>
